/*
  PDP configurator (DPO replacement).
  - Pill rows are server-rendered by snippets/pdp-configurator.liquid.
  - Shopify holds three options; the combined third option ("2.2m Primed") is shown as
    two rows (Length, Priming) using the split described in the mrm.configurator metafield.
  - Every click fetches sections/pdp-configurator-state.liquid via the Section Rendering
    API with ?option_values=… (one request, previous one aborted): availability of every
    pill for a partial selection, the variant + price for a complete one.
  - Unavailable pills stay visible and explain themselves with the rule message from the
    metafield (no browser alert(), unlike DPO).
  - Add to cart is a single POST /cart/add.js, then GET /cart.js and a `cartcount:update`
    event (Empire updates the count; the cart drawer, when present, opens on it).
    Edit-from-cart: ?variant=<id>&edit=<line key>&qty=<n> swaps the line instead.
*/
(function () {
  'use strict';

  var root = document.querySelector('[data-mrc]');
  if (!root || root.dataset.mrcReady) return;
  root.dataset.mrcReady = '1';

  var config = JSON.parse(root.querySelector('[data-mrc-config]').textContent);
  var valueIds = JSON.parse(root.querySelector('[data-mrc-values]').textContent); // option name → { value name → id }
  var rows = Array.prototype.slice.call(root.querySelectorAll('[data-mrc-row]'));
  var hiddenId = root.querySelector('[data-mrc-variant-id]');
  var status = root.querySelector('[data-mrc-status]');
  var form = root.closest('form');
  var atcButton = form ? form.querySelector('[data-product-atc]') : null;
  var atcText = atcButton ? atcButton.querySelector('.atc-button--text') : null;
  var priceRoot = document.querySelector('[data-product-pricing]');
  // Re-queried on every render, never cached: the VAT sync in product-price.liquid can rebuild the
  // container's markup, and a cached node then points at something no longer in the document — the
  // price would silently stop updating.
  function findPriceSource() {
    return priceRoot ? (priceRoot.querySelector('[data-vat-source] [data-price]') || priceRoot.querySelector('[data-price]')) : null;
  }
  var money = new Intl.NumberFormat('en-GB', { style: 'currency', currency: (window.Theme && window.Theme.currency) || 'GBP' });
  // Edit-from-cart: ?variant=<id>&edit=<line key>&qty=<n> (Liquid cannot read query params, so JS does)
  var params = new URLSearchParams(window.location.search);
  var editKey = params.get('edit') || '';
  var editQty = parseInt(params.get('qty'), 10) || 0;
  // Read from data-atc-label, never from the button's text: on a configurable product the server
  // renders "Choose your options" there, so taking the label from the DOM captured the placeholder
  // and the button still read "Choose your options" once a size was picked and it was ready to
  // click. product-form.liquid renders the attribute on every add-to-cart button.
  var atcLabel = editKey
    ? 'Update basket'
    : ((atcButton && atcButton.getAttribute('data-atc-label')) || 'Add to cart');
  var chooseLabel = 'Choose your options';

  // Row label → selected value. Rows for a combined option share data-mrc-option.
  var selection = {};
  rows.forEach(function (row) {
    var picked = row.querySelector('.mrc__pill.is-selected');
    selection[row.dataset.mrcRow] = picked ? picked.dataset.value : null;
  });
  var variant = null;

  function optionName(row) { return row.dataset.mrcOption; }
  function rowsFor(option) { return rows.filter(function (r) { return optionName(r) === option; }); }
  function splitDef(option) {
    for (var i = 0; i < config.options.length; i++) if (config.options[i].name === option && config.options[i].split) return config.options[i].split;
    return null;
  }

  /** Combined value name for an option from the current row selections, or null while incomplete. */
  function combinedName(option) {
    var parts = rowsFor(option);
    if (parts.length === 1) return selection[parts[0].dataset.mrcRow];
    var dim = null, fin = null;
    parts.forEach(function (r) {
      if (r.dataset.mrcPart === 'dimension') dim = selection[r.dataset.mrcRow];
      if (r.dataset.mrcPart === 'finish') fin = selection[r.dataset.mrcRow];
    });
    return dim && fin ? dim + ' ' + fin : null;
  }

  function selectedValueIds() {
    var ids = [];
    Object.keys(valueIds).forEach(function (option) {
      var name = combinedName(option);
      if (name && valueIds[option][name]) ids.push(valueIds[option][name]);
    });
    return ids;
  }

  function isComplete() {
    return rows.every(function (r) { return !!selection[r.dataset.mrcRow]; });
  }

  /** The rule (if any) that forbids adding `value` on `row` to the current selection. */
  function ruleFor(row, value) {
    var candidate = {};
    rows.forEach(function (r) { candidate[r.dataset.mrcRow] = selection[r.dataset.mrcRow]; });
    candidate[row.dataset.mrcRow] = value;
    for (var i = 0; i < config.rules.length; i++) {
      var match = config.rules[i].match, hit = true;
      Object.keys(match).forEach(function (label) {
        if (!candidate[label] || match[label].indexOf(candidate[label]) < 0) hit = false;
      });
      if (hit) return config.rules[i];
    }
    return null;
  }

  function setMessage(row, text) {
    var el = row.querySelector('[data-mrc-message]');
    if (!el) return;
    if (text) { el.textContent = text; el.hidden = false; } else { el.textContent = ''; el.hidden = true; }
  }

  function renderRow(row) {
    var current = selection[row.dataset.mrcRow];
    var valueEl = row.querySelector('[data-mrc-current]');
    if (valueEl) valueEl.textContent = current || '';
    row.querySelectorAll('[data-mrc-pill]').forEach(function (pill) {
      var on = pill.dataset.value === current;
      pill.classList.toggle('is-selected', on);
      pill.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }

  /** Apply Shopify's availability (from the state section) to every pill. */
  function applyAvailability(options) {
    var byOption = {};
    options.forEach(function (o) { byOption[o.name] = o.values; });
    rows.forEach(function (row) {
      var values = byOption[optionName(row)] || [];
      var split = splitDef(optionName(row));
      row.querySelectorAll('[data-mrc-pill]').forEach(function (pill) {
        var name = pill.dataset.value, available;
        if (!split) {
          available = values.some(function (v) { return v.name === name && v.available; });
        } else if (row.dataset.mrcPart === 'dimension') {
          // exact "<dimension> <finish>" names only — "Non Primed" ends with " Primed", so no prefix/suffix tests
          var fin = selection[rowsFor(optionName(row)).filter(function (r) { return r.dataset.mrcPart === 'finish'; })[0].dataset.mrcRow];
          var finishes = fin ? [fin] : split.finish.values;
          available = finishes.some(function (f) { return values.some(function (v) { return v.available && v.name === name + ' ' + f; }); });
        } else {
          var dim = selection[rowsFor(optionName(row)).filter(function (r) { return r.dataset.mrcPart === 'dimension'; })[0].dataset.mrcRow];
          var dims = dim ? [dim] : split.dimension.values;
          available = dims.some(function (d) { return values.some(function (v) { return v.available && v.name === d + ' ' + name; }); });
        }
        pill.classList.toggle('is-unavailable', !available);
        pill.setAttribute('aria-disabled', available ? 'false' : 'true');
      });
    });
  }

  function renderPrice() {
    if (!priceRoot) return;
    var pence = variant ? variant.price : parseInt(root.dataset.priceMin, 10);
    if (!isFinite(pence)) return;
    priceRoot.classList.toggle('is-from-price', !variant);

    // Hand the VAT sync the NUMBER. It used to be told by writing formatted text into the source
    // span and letting a MutationObserver re-parse it, which meant the price made a round trip
    // through rendered text on every change — the same path that let a foreign writer's output be
    // read back as a new Inc VAT figure and divided by 1.2 again.
    document.dispatchEvent(new CustomEvent('mrm:price', { detail: { pence: pence } }));

    // Still write the text, for the sticky bar and anything else reading the source span, and in
    // case this theme's price snippet predates the mrm:price listener.
    var priceSource = findPriceSource();
    if (priceSource) priceSource.textContent = money.format(pence / 100);
  }

  function renderAtc() {
    if (!atcButton) return;
    var ready = !!(variant && variant.available);
    atcButton.disabled = !ready;
    atcButton.classList.toggle('disabled', !ready);
    if (atcText) atcText.textContent = ready ? atcLabel : (isComplete() ? 'Unavailable' : chooseLabel);
    if (hiddenId) hiddenId.value = ready ? variant.id : '';
  }

  function setStatus(text, isError) {
    if (!status) return;
    status.textContent = text || '';
    status.hidden = !text;
    status.classList.toggle('is-error', !!isError);
  }

  // Responses are small and cheap, so stale ones are simply ignored by sequence number instead
  // of aborting in-flight requests (aborting a fetch mid-body surfaces as an unhandled
  // AbortError in Chromium around navigations).
  var requestSeq = 0;
  function refresh() {
    var mySeq = ++requestSeq;
    var ids = selectedValueIds();
    var url = root.dataset.productUrl.split('?')[0] + '?section_id=' + encodeURIComponent(root.dataset.stateSection) + (ids.length ? '&option_values=' + ids.join(',') : '');
    root.classList.add('is-loading');
    fetch(url, { headers: { Accept: 'text/html' } })
      .then(function (r) { if (!r.ok) throw new Error('state ' + r.status); return r.text(); })
      .then(function (html) {
        if (mySeq !== requestSeq) return; // a newer click superseded this response
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var node = doc.querySelector('[data-mrc-state]');
        if (!node) throw new Error('state section missing');
        var state = JSON.parse(node.textContent);
        variant = isComplete() ? state.variant : null;
        applyAvailability(state.options);
        renderPrice();
        renderAtc();
        root.classList.remove('is-loading');
      })
      .catch(function (err) {
        if (mySeq !== requestSeq) return;
        root.classList.remove('is-loading');
        setStatus('Could not update the price. Please try again.', true);
      });
  }

  rows.forEach(function (row) {
    row.addEventListener('click', function (event) {
      var pill = event.target.closest('[data-mrc-pill]');
      if (!pill) return;
      var value = pill.dataset.value;
      if (pill.classList.contains('is-unavailable')) {
        var rule = ruleFor(row, value);
        setMessage(row, rule ? rule.message : 'This combination is not available.');
        return;
      }
      if (selection[row.dataset.mrcRow] === value) return; // re-clicking the chosen pill is a no-op, as in the DPO chooser
      rows.forEach(function (r) { setMessage(r, null); });
      setStatus('');
      selection[row.dataset.mrcRow] = value;
      renderRow(row);
      variant = null;
      renderAtc();
      refresh();
    });
  });

  /* ---------- add to cart: one request, no polling, no redirect unless there is no drawer ---------- */
  function quantity() {
    var input = form ? form.querySelector('[data-quantity-input], input[name="quantity"]') : null;
    var q = input ? parseInt(input.value, 10) : 1;
    return q > 0 ? q : 1;
  }

  function post(url, body) {
    return fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) })
      .then(function (r) { return r.json().then(function (data) { if (!r.ok) throw new Error(data.description || data.message || ('HTTP ' + r.status)); return data; }); });
  }

  function addToCart() {
    if (!variant || !variant.available) { setStatus('Please choose ' + rows.filter(function (r) { return !selection[r.dataset.mrcRow]; }).map(function (r) { return r.dataset.mrcRow.toLowerCase(); }).join(', ') + '.', true); return; }
    var qty = quantity();
    atcButton.classList.add('is-adding');
    atcButton.disabled = true;
    var flow = editKey ? post('/cart/change.js', { id: editKey, quantity: 0 }) : Promise.resolve();
    flow
      .then(function () { return post('/cart/add.js', { items: [{ id: variant.id, quantity: qty }] }); })
      .then(function () { return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); }); })
      .then(function (cart) {
        atcButton.classList.remove('is-adding');
        atcButton.disabled = false;
        window.dispatchEvent(new CustomEvent('cartcount:update', { detail: cart }));
        if (!document.querySelector('[data-cart-drawer]')) window.location.href = '/cart';
      })
      .catch(function (err) {
        atcButton.classList.remove('is-adding');
        atcButton.disabled = false;
        setStatus(err.message || 'Could not add to basket.', true);
      });
  }

  if (form) {
    form.setAttribute('data-mrc-form', '');
    // Capture on document so this runs before Empire's own product-form submit handler.
    document.addEventListener('submit', function (event) {
      if (event.target !== form) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      addToCart();
    }, true);
    if (editQty > 0) {
      var qtyInput = form.querySelector('[data-quantity-input], input[name="quantity"]');
      if (qtyInput) { qtyInput.value = editQty; qtyInput.dispatchEvent(new Event('change', { bubbles: true })); }
    }
  }

  rows.forEach(renderRow);
  renderAtc();
  refresh();
})();
